import { ISbStoriesParams } from "@storyblok/react/rsc";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  isAfter,
  isBefore,
  startOfDay,
  startOfMonth,
  subSeconds,
} from "date-fns";
import { format, toDate } from "date-fns-tz";

import type { EventStoryblok } from "@/component-types-sb";

export type EventOccurrence = {
  key: string;
  event: EventStoryblok;
  occurrenceDate: Date;
};

export type EventRange = {
  key: string;
  event: EventStoryblok;
  startDate: Date;
  endDate: Date;
};

export const EVENT_TIME_ZONE = "Europe/Berlin";

const parseEventDate = (value?: string) => {
  if (!value) {
    return null;
  }

  return toDate(value, { timeZone: "GMT" });
};

const getEventKey = (event: EventStoryblok) =>
  String(event.uuid || event.id || event.full_slug || event.slug || event.name);

const getUniqueEvents = (events: EventStoryblok[]) =>
  Array.from(new Map(events.map((event) => [getEventKey(event), event])).values());

const getEventRange = (event: EventStoryblok) => {
  const startDate = parseEventDate(event.content?.date);

  if (!startDate) {
    return null;
  }

  const untilDate = parseEventDate(event.content?.until) || startDate;
  const endDate = isAfter(untilDate, startDate) ? untilDate : startDate;

  return {
    startDate,
    endDate,
  };
};

export const getMonthEventQueries = (
  date: Date,
  campsiteId?: string
): ISbStoriesParams[] => {
  const monthStart = startOfMonth(date);
  const monthStartBoundary = format(
    subSeconds(monthStart, 1),
    "yyyy-MM-dd HH:mm",
    {
      timeZone: EVENT_TIME_ZONE,
    }
  );
  const nextMonthStartBoundary = format(
    startOfMonth(addMonths(date, 1)),
    "yyyy-MM-dd HH:mm",
    {
      timeZone: EVENT_TIME_ZONE,
    }
  );

  return [
    {
      filter_query: {
        date: {
          gt_date: monthStartBoundary,
          lt_date: nextMonthStartBoundary,
        },
        campsite: { in: campsiteId || null },
      },
    },
    {
      filter_query: {
        date: {
          lt_date: nextMonthStartBoundary,
        },
        until: {
          gt_date: monthStartBoundary,
        },
        campsite: { in: campsiteId || null },
      },
    },
  ];
};

export const getUpcomingEventQueries = (
  date: Date,
  campsiteId?: string,
  monthsAhead = 2
): ISbStoriesParams[] => {
  const todayBoundary = format(
    subSeconds(startOfDay(date), 1),
    "yyyy-MM-dd HH:mm",
    {
      timeZone: EVENT_TIME_ZONE,
    }
  );
  const horizonBoundary = format(
    endOfMonth(addMonths(date, monthsAhead)),
    "yyyy-MM-dd HH:mm",
    {
      timeZone: EVENT_TIME_ZONE,
    }
  );

  return [
    {
      filter_query: {
        date: {
          gt_date: todayBoundary,
          lt_date: horizonBoundary,
        },
        campsite: { in: campsiteId || null },
      },
    },
    {
      filter_query: {
        date: {
          lt_date: horizonBoundary,
        },
        until: {
          gt_date: todayBoundary,
        },
        campsite: { in: campsiteId || null },
      },
    },
  ];
};

export const expandEventsForMonth = (
  events: EventStoryblok[],
  date: Date
): EventOccurrence[] => {
  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);

  return getUniqueEvents(events)
    .flatMap((event) => {
      const eventRange = getEventRange(event);

      if (!eventRange) {
        return [];
      }

      const visibleStart = isBefore(eventRange.startDate, monthStart)
        ? monthStart
        : eventRange.startDate;
      const visibleEnd = isAfter(eventRange.endDate, monthEnd)
        ? monthEnd
        : eventRange.endDate;
      const intervalStart = startOfDay(visibleStart);
      const intervalEnd = startOfDay(visibleEnd);

      if (isAfter(intervalStart, intervalEnd)) {
        return [];
      }

      return eachDayOfInterval({
        start: intervalStart,
        end: intervalEnd,
      }).map((occurrenceDate) => ({
        key: `${getEventKey(event)}-${format(occurrenceDate, "yyyy-MM-dd")}`,
        event,
        occurrenceDate,
      }));
    })
    .sort((left, right) => {
      const dateDiff =
        left.occurrenceDate.getTime() - right.occurrenceDate.getTime();

      if (dateDiff !== 0) {
        return dateDiff;
      }

      return (left.event.name || "").localeCompare(right.event.name || "");
    });
};

export const collapseUpcomingEventsToRanges = (
  events: EventStoryblok[],
  date: Date
): EventRange[] => {
  const today = startOfDay(date);

  return getUniqueEvents(events)
    .flatMap((event) => {
      const eventRange = getEventRange(event);

      if (!eventRange) {
        return [];
      }

      if (eventRange.endDate.getTime() < today.getTime()) {
        return [];
      }

      return [
        {
          key: getEventKey(event),
          event,
          startDate: eventRange.startDate,
          endDate: eventRange.endDate,
        },
      ];
    })
    .sort((left, right) => {
      const leftSortDate = left.startDate.getTime() < today.getTime()
        ? today.getTime()
        : left.startDate.getTime();
      const rightSortDate = right.startDate.getTime() < today.getTime()
        ? today.getTime()
        : right.startDate.getTime();

      if (leftSortDate !== rightSortDate) {
        return leftSortDate - rightSortDate;
      }

      return (left.event.name || "").localeCompare(right.event.name || "");
    });
};
